import { useState, useCallback, useMemo, DragEvent, ChangeEvent } from 'react';
import useAppContext from '../../../hooks/useAppContext';
import { AlertType } from '../../../design-system/DSAlert';
import { readAudioDurationInSeconds } from '../../../utils/uploads';

type FileValidationConfig = {
  acceptedMimeTypes: readonly string[];
  maxSizeBytes?: number;
  maxDurationSeconds?: number;
  fileTypeLabel?: string;
};

type UseDragAndDropProps = {
  onFileAccepted: (file: File) => void;
  validation: FileValidationConfig;
  enabled?: boolean;
};

type DragHandlers = {
  onDragOver: (e: DragEvent) => void;
  onDragEnter: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
};

export const createSyntheticFileEvent = (
  file: File,
): ChangeEvent<HTMLInputElement> =>
  ({
    target: { files: [file] as unknown as FileList },
  }) as ChangeEvent<HTMLInputElement>;

const useInputsDragAndDrop = ({
  onFileAccepted,
  validation,
  enabled = true,
}: UseDragAndDropProps) => {
  const { showAlert } = useAppContext();
  const [isDragging, setIsDragging] = useState(false);

  const validateAudioDuration = useCallback(
    async (file: File): Promise<boolean> => {
      const { maxDurationSeconds } = validation;
      if (!maxDurationSeconds) return true;

      const duration = await readAudioDurationInSeconds(file);

      if (duration === undefined) {
        showAlert({
          alertType: AlertType.ERROR,
          message: 'Failed to load audio file. Please try a different file.',
        });
        return false;
      }

      if (duration > maxDurationSeconds) {
        showAlert({
          alertType: AlertType.ERROR,
          message: `Audio duration exceeds ${maxDurationSeconds} seconds.`,
        });
        return false;
      }

      return true;
    },
    [validation, showAlert],
  );

  const validateFile = useCallback(
    async (file: File): Promise<boolean> => {
      const {
        acceptedMimeTypes,
        maxSizeBytes,
        maxDurationSeconds,
        fileTypeLabel,
      } = validation;

      if (!acceptedMimeTypes.includes(file.type)) {
        showAlert({
          alertType: AlertType.ERROR,
          message: `Invalid file type. Please select ${fileTypeLabel || 'a valid'} file.`,
        });
        return false;
      }

      if (maxSizeBytes && file.size > maxSizeBytes) {
        const maxSizeMB = maxSizeBytes / (1024 * 1024);
        showAlert({
          alertType: AlertType.ERROR,
          message: `File size exceeds ${maxSizeMB}MB. Please select a smaller file.`,
        });
        return false;
      }

      if (maxDurationSeconds) {
        return validateAudioDuration(file);
      }

      return true;
    },
    [validation, showAlert, validateAudioDuration],
  );

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file || !enabled) return;
      const isValid = await validateFile(file);
      if (isValid) onFileAccepted(file);
    },
    [enabled, validateFile, onFileAccepted],
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [enabled],
  );

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    [enabled],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setIsDragging(false);
    },
    [enabled],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFile(e.dataTransfer.files?.[0]);
    },
    [enabled, handleFile],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!enabled) return;
      handleFile(e.target.files?.[0]);
    },
    [enabled, handleFile],
  );

  const dragHandlers: DragHandlers = useMemo(
    () => ({
      onDragOver: handleDragOver,
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    }),
    [handleDragOver, handleDragEnter, handleDragLeave, handleDrop],
  );

  return {
    isDragging,
    dragHandlers,
    handleInputChange,
    handleFile,
  };
};

export default useInputsDragAndDrop;
